import React, { useCallback, useEffect, useRef } from 'react';
import { DateInput, Text } from '@goodrx/matisse';
import { debounce } from 'lodash';
import moment from 'moment';
import { useSynapse } from 'app/lib/synaptik';
import { OnboardingField, OnboardingFieldProps } from './OnboardingField';
import { ServiceData } from '@goodrx/heydoctor-sdk';

const MIN_AGE = 18;

interface ValidateProps {
  dob?: string;
  service: ServiceData;
}

interface ValidateReturn {
  valid: boolean;
  errorMessage?: string;
}

const validateDob = ({ dob, service }: ValidateProps): ValidateReturn => {
  const date = moment(dob, 'MM/DD/YYYY', true);
  if (!date.isValid()) {
    return { valid: false };
  }

  const ageInYears = moment().diff(date, 'years');
  if (service) {
    if (ageInYears < service.minAge || ageInYears > service.maxAge) {
      return {
        valid: false,
        errorMessage: `You must be between ${service.minAge} and ${service.maxAge} years old to use this service`,
      };
    }
  } else if (ageInYears < MIN_AGE) {
    return {
      valid: false,
      errorMessage: `You must be at least ${MIN_AGE} years old to use this service`,
    };
  }

  return { valid: true };
};

const DateOfBirth: React.FC<OnboardingFieldProps & { service: ServiceData }> = ({
  values,
  onChange,
  error,
  service,
}) => {
  const validatedDob = validateDob({ dob: values.dob, service });

  const handleChange = useCallback(
    (_name: string, dob: string) => {
      onChange({
        values: { dob },
        validate: () => {
          return validateDob({ dob, service });
        },
      });
    },
    [service, onChange]
  );

  const handleServiceChangeRef = useRef(useCallback(() => handleChange('dob', values.dob), [values.dob, handleChange]));

  useEffect(() => {
    handleServiceChangeRef.current();
  }, [service]);

  return (
    <>
      <Text as="h3" fontSize={18} mt={3} mb={2}>
        Date of birth
      </Text>
      <DateInput
        type="tel"
        value={values.dob}
        name="dateOfBirth"
        placeholder="MM/DD/YYYY"
        onChange={debounce(handleChange, 250)}
        tabIndex={0}
        rightIcon={values.dob ? (error ? 'close' : validatedDob.valid ? 'check' : null) : null}
        rightIconProps={{
          color: error ? 'red' : 'secondary',
        }}
      />
      {error && (
        <Text as="small" color="red" ml={1} fontWeight={500}>
          {error}
        </Text>
      )}
    </>
  );
};

const DateOfBirthWrapper: React.FC<{ service?: ServiceData }> = ({ service: passedService }) => {
  const service = useSynapse(({ services }) => passedService || services.getIntendedService());

  return (
    <OnboardingField errorFieldName="dob" fields={['dob']}>
      {(props) => <DateOfBirth {...props} service={service} />}
    </OnboardingField>
  );
};

export { DateOfBirthWrapper as DateOfBirth };
